import axios from "axios";
import { API_CONSTANTS } from "./api-constant";
import { isServerError, triggerGlobalError } from "./globalErrorBridge";
import {
  getAccessToken,
  getRefreshToken,
  getLoginPath,
  updateStoredTokens,
  clearSession,
} from "../services/auth";

const SERVER_ERROR_MESSAGE = "Something went wrong, please try again later";

const apiClient = axios.create();

let idleTimerReset = null;
let isRefreshing = false;
let failedQueue = [];

export const setIdleTimerReset = (resetCallback) => {
  idleTimerReset = resetCallback;
};

const clearSessionAndRedirect = () => {
  const loginPath = getLoginPath();

  clearSession();
  window.location.href = loginPath;
};

const isUnauthorizedData = (data) =>
  data?.statusCode === 401 ||
  data?.status === 401 ||
  data?.error?.statusCode === 401 ||
  data?.error?.status === 401;

const isServerErrorData = (data) =>
  isServerError(data?.statusCode) ||
  isServerError(data?.status) ||
  isServerError(data?.error?.statusCode) ||
  isServerError(data?.error?.status);

const createUnauthorizedError = (response) => {
  const error = new Error("Unauthorized");
  error.config = response.config;
  error.response = {
    ...response,
    status: 401,
  };

  return error;
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Shared so "Stay Logged In" and the 401 interceptor can't both send the same
// refresh token: with rotation, the second request would fail and end the session.
let refreshInFlight = null;

export const refreshAccessToken = () => {
  if (!refreshInFlight) {
    refreshInFlight = requestNewTokens().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

const requestNewTokens = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await axios({
    method: "GET",
    url: `${API_CONSTANTS.REFRESH_ACCESS_TOKEN}?refreshToken=${encodeURIComponent(refreshToken)}`,
    headers: {
      "Content-Type": "application/json",
    },
  });

  const { statusCode, data, message } = response.data || {};

  if (statusCode !== 200 || !data?.accessToken) {
    throw new Error(message || "Failed to refresh token");
  }

  updateStoredTokens(data);

  return data.accessToken;
};

export const normalizeApiError = (error) => {
  const responseData = error?.response?.data;

  if (responseData) {
    return responseData;
  }

  return {
    statusCode: error?.response?.status || 0,
    message: error?.message || SERVER_ERROR_MESSAGE,
  };
};

apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (!config.skipAuth && token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (idleTimerReset) {
      idleTimerReset();
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    if (idleTimerReset) {
      idleTimerReset();
    }

    if (isServerError(response.status) || isServerErrorData(response.data)) {
      triggerGlobalError(SERVER_ERROR_MESSAGE);
    }

    if (!response.config?.skipRefresh && isUnauthorizedData(response.data)) {
      return Promise.reject(createUnauthorizedError(response));
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};

    if (isServerError(error.response?.status)) {
      triggerGlobalError(SERVER_ERROR_MESSAGE);
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.skipRefresh
    ) {
      // Set before queueing too, so a queued request is also retried only once.
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        processQueue(null, newToken);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearSessionAndRedirect();

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
